import { NpsQuestion, NpsQuestionnaire } from '../../store/reducers/nps/type';

type FlatQuestion = {
  question: NpsQuestion;
  key: string;
  parentKey?: string;
  conditionalKey?: string;
};

export type ChecklistAnswer = Record<string, { key: string; value: string } | undefined>;

export type QuestionAnswers = Record<
  string,
  | { type: 'score'; value: number | undefined }
  | { type: 'checklist'; value: ChecklistAnswer }
  | { type: 'open'; value: string }
>;

export type NpsResponseDetail = {
  questionId: string;
  questionText: string;
  answer: string[];
};

function buildFlatQuestions(
  questions: NpsQuestion[],
  answers: QuestionAnswers,
  parentKey?: string,
  conditionalKey?: string
): FlatQuestion[] {
  const result: FlatQuestion[] = [];

  questions.forEach((question, idx) => {
    const key = parentKey ? `${parentKey}_CQ${String(idx).padStart(3, '0')}` : `Q${String(idx).padStart(3, '0')}`;
    result.push({ question, key, parentKey, conditionalKey });

    if (question.isConditional && question.conditionalQuestion?.conditions) {
      const answer = answers[key];

      if (answer?.type === 'score' && answer.value !== undefined) {
        const conditionKey = Object.keys(question.conditionalQuestion.conditions).find(condition => {
          if (condition.includes('-')) {
            const [start, end] = condition.split('-').map(Number);
            return start !== undefined && end !== undefined && answer.value! >= start && answer.value! <= end;
          }

          return Number(condition) === answer.value;
        });

        if (conditionKey) {
          result.push(
            ...buildFlatQuestions(
              question.conditionalQuestion.conditions[conditionKey] ?? [],
              answers,
              key,
              conditionKey
            )
          );
        }
      }
    }
  });

  return result;
}

function extractAnswer(answer: QuestionAnswers[string]): string[] {
  if (answer.type === 'score') {
    return answer.value !== undefined ? [String(answer.value)] : [];
  }

  if (answer.type === 'open') {
    return answer.value ? [answer.value] : [];
  }

  return Object.values(answer.value)
    .filter(Boolean)
    .map(item => {
      if (!item) return '';
      if (item.key.toLowerCase().includes('other')) {
        return `other - ${item.value ?? ''}`;
      }

      return item.value;
    })
    .filter(Boolean);
}

function constructQuestionId(question: FlatQuestion): string {
  const rootQuestionKey = question.key.split('_')[0];
  const rootQuestionIndex = Number(rootQuestionKey.replace('Q', ''));

  let questionId = `${rootQuestionIndex + 1}_1`;

  if (question.parentKey && question.conditionalKey) {
    questionId += `_${question.conditionalKey}`;
  }

  return questionId;
}

export function buildNpsResponseDetails(
  questionnaire: NpsQuestionnaire,
  answers: QuestionAnswers
): NpsResponseDetail[] {
  const flatQuestions = buildFlatQuestions(questionnaire.questions, answers);

  return flatQuestions.flatMap(question => {
    const answer = answers[question.key];

    if (!answer) return [];

    return [
      {
        questionId: constructQuestionId(question),
        questionText: question.question.content,
        answer: extractAnswer(answer)
      }
    ];
  });
}
